import { AnalyticsFeatureEvent } from '@shared/models/analytics/AnalyticsFeatureEventType';
import IUserAccess from '@shared/models/components/IUserAccess';
import UserDeviceProfileModel from '@shared/models/deviceProfiles/UserDeviceProfileModel';
import UserDeviceModel from '@shared/models/devices/UserDeviceModel';
import AutomationRuleModel from '@shared/models/organizations/AutomationRuleModel';
import UpdateUserLocaleRequest from '@shared/models/users/UpdateUserLocaleRequest';
import UserWithCapabilitiesModel from '@shared/models/users/UserWithCapabilitiesModel';

// Define a type alias for the keys of EventMap
export type EventMapKey = keyof EventMap;

// Define the EventMap interface for clarity
interface EventMap {
    alertSuccess: { message: string; timeout?: number };
    alertError: { message: string; timeout?: number };
    alertWarning: { message: string; timeout?: number };
    analyticsTrackEvent: { eventName: AnalyticsFeatureEvent; data: { [key: string]: any } };
    audioStream: { audio: Float32Array; typeCommands?: Set<string> };
    hotkeyCommand: { command: string };
    userProfileAdded: { profile: UserDeviceProfileModel }; // to handle local additions of user profiles from edit profile page
    userProfileUpdated: { profile: UserDeviceProfileModel }; // to handle local updates to user profiles from edit profile page
    userProfileDeleted: { profileId: string }; // to handle local deletions of user profiles from edit profile page
    profileDeleted: { profile: UserDeviceProfileModel | undefined };
    stopListening: { deviceId: string };
    voiceCommandFinal: { text: string };
    voiceCommandPartial: { text: string };
    voiceCommandPaused: { text: string; resultType: 'partial' | 'final' };
    voiceCommandDictation: { text: string; commandOnly: boolean };
    voiceCommandHandled: void;
    nowPlaying: { videoId: string };
    cameraCommandHandled: void;
    voiceStopped: void;
    updateUserLicenseAccess: void;
    userLicenseChangeLocal: IUserAccess;
    userLocalePreferencesUpdated: { localePreferences: UpdateUserLocaleRequest };
    userFeatureFlagsUpdated: { featureFlags: string[] };
    userUpdated: {
        user: UserWithCapabilitiesModel;
    };
    currentDeviceUpdated: { device: UserDeviceModel | undefined; profile: UserDeviceProfileModel | null };
    remoteDeviceCommand: { command: string };
    navigateTo: { path: string; params?: Record<string, string | number | boolean | undefined> };

    // For whisper start/stop, since no payload is needed, use void:
    whisperInitialize: void;
    whisperStart: void;
    whisperStop: void;
    whisperTerminate: void;
    tourStarted: { initiator: string; tourKey: string; stepIndex: number };
    tourEnded: { initiator: string; tourKey: string; stepIndex: number; skipped?: boolean };
    tourStepChanged: { initiator: string; tourKey: string; stepIndex: number };
    tourPaused: boolean; // New event for pausing the tour
    tourFocusSource: void; // New event for focusing the tour source

    // IPC events
    'force-reconnect': void;
    'license-trial-started': void;
    'default-profiles-updated': UserDeviceProfileModel[];
    'update-voice-dictation-controls': Set<string>;
    'remove-device': { device: UserDeviceModel };
    'device-added': { device: UserDeviceModel };
}

// General event callback type based on the specific event key
export type EventCallback<K extends EventMapKey> = (data: EventMap[K]) => void;

// Pre-initialize the listeners object for every event in EventMap,
// with explicit type assertions for each empty array.
const listeners: { [K in EventMapKey]: EventCallback<K>[] } = {
    analyticsTrackEvent: [] as EventCallback<'analyticsTrackEvent'>[],
    alertSuccess: [] as EventCallback<'alertSuccess'>[],
    alertError: [] as EventCallback<'alertError'>[],
    alertWarning: [] as EventCallback<'alertWarning'>[],
    audioStream: [] as EventCallback<'audioStream'>[],
    hotkeyCommand: [] as EventCallback<'hotkeyCommand'>[],
    stopListening: [] as EventCallback<'stopListening'>[],
    userProfileAdded: [] as EventCallback<'userProfileAdded'>[],
    userProfileUpdated: [] as EventCallback<'userProfileUpdated'>[],
    userProfileDeleted: [] as EventCallback<'userProfileDeleted'>[],
    profileDeleted: [] as EventCallback<'profileDeleted'>[],
    voiceCommandFinal: [] as EventCallback<'voiceCommandFinal'>[],
    voiceCommandPartial: [] as EventCallback<'voiceCommandPartial'>[],
    voiceCommandPaused: [] as EventCallback<'voiceCommandPaused'>[],
    voiceCommandDictation: [] as EventCallback<'voiceCommandDictation'>[],
    voiceCommandHandled: [] as EventCallback<'voiceCommandHandled'>[],
    cameraCommandHandled: [] as EventCallback<'cameraCommandHandled'>[],
    voiceStopped: [] as EventCallback<'voiceStopped'>[],
    nowPlaying: [] as EventCallback<'nowPlaying'>[],
    updateUserLicenseAccess: [] as EventCallback<'updateUserLicenseAccess'>[],
    userLicenseChangeLocal: [] as EventCallback<'userLicenseChangeLocal'>[],
    userLocalePreferencesUpdated: [] as EventCallback<'userLocalePreferencesUpdated'>[],
    userFeatureFlagsUpdated: [] as EventCallback<'userFeatureFlagsUpdated'>[],
    userUpdated: [] as EventCallback<'userUpdated'>[],
    whisperInitialize: [] as EventCallback<'whisperInitialize'>[],
    whisperStart: [] as EventCallback<'whisperStart'>[],
    whisperStop: [] as EventCallback<'whisperStop'>[],
    whisperTerminate: [] as EventCallback<'whisperTerminate'>[],
    currentDeviceUpdated: [] as EventCallback<'currentDeviceUpdated'>[],
    remoteDeviceCommand: [] as EventCallback<'remoteDeviceCommand'>[],
    navigateTo: [] as EventCallback<'navigateTo'>[],
    tourStarted: [] as EventCallback<'tourStarted'>[],
    tourEnded: [] as EventCallback<'tourEnded'>[],
    tourStepChanged: [] as EventCallback<'tourStepChanged'>[],
    tourPaused: [] as EventCallback<'tourPaused'>[], // New event for pausing the tour
    tourFocusSource: [] as EventCallback<'tourFocusSource'>[],

    // from IPC events
    'force-reconnect': [] as EventCallback<'force-reconnect'>[],
    'license-trial-started': [] as EventCallback<'license-trial-started'>[],
    'default-profiles-updated': [] as EventCallback<'default-profiles-updated'>[],
    'update-voice-dictation-controls': [] as EventCallback<'update-voice-dictation-controls'>[],
    'remove-device': [] as EventCallback<'remove-device'>[],
    'device-added': [] as EventCallback<'device-added'>[],
};

// A helper function to narrow the type of the listeners array for a given event key
function getListeners<K extends EventMapKey>(event: K): EventCallback<K>[] {
    return listeners[event] as EventCallback<K>[];
}

// Subscribe function with strong typing
export function subscribe<K extends EventMapKey>(event: K, callback: EventCallback<K>): () => void {
    // Add the callback to the event's listener array
    getListeners(event).push(callback);

    // Instead of reassigning listeners[event] with a filtered array,
    // we remove the callback from the existing array in place.
    return () => {
        const arr = getListeners(event);
        const index = arr.indexOf(callback);
        if (index !== -1) {
            arr.splice(index, 1);
        }
    };
}

// Publish function with strong typing
export function publish<K extends keyof EventMap>(
    event: K,
    ...payload: EventMap[K] extends void ? [] : [data: EventMap[K]]
): void {
    const data = payload[0] as EventMap[K] | undefined;
    (listeners[event] as EventCallback<K>[] | undefined)?.forEach((cb) => cb(data!));
}

function hasInitiator(obj: unknown): obj is { initiator: string } {
    return !!obj && typeof obj === 'object' && 'initiator' in obj;
}

// Forwarding service that listens to specific events and forwards them to a send function
export function relayLocalEventsToRendererBus<K extends EventMapKey>(
    eventNames: K[],
    initiatorRef: React.MutableRefObject<string>,
    sendFn: (channel: K, data: EventMap[K]) => void
): () => void {
    const unsubscribers = eventNames.map((eventName) =>
        subscribe(eventName, (event: EventMap[K]) => {
            if (hasInitiator(event) && event.initiator === initiatorRef.current) {
                sendFn(eventName, event);
            }
        })
    );

    return () => unsubscribers.forEach((unsub) => unsub());
}
